import React, { useState, useEffect } from 'react';
import { Users, Phone, Mail, Calendar, CreditCard, Check, X, ArrowRight, MessageSquare } from 'lucide-react';
import apiService from './services/api';
import { Panel, LoadingSpinner } from './components';

function PersonCard({ person, isRecommendedKeep }) {
  const hasPhone = !!person.phone_number;
  const hasClient = !!person.has_client;
  const hasAppts = (person.appointment_count || 0) > 0;

  return (
    <div className={`rounded-lg border-2 p-3 ${
      isRecommendedKeep ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-white'
    }`}>
      {isRecommendedKeep && (
        <div className="text-[10px] font-semibold text-green-700 uppercase tracking-wide mb-1.5">Recommended to keep</div>
      )}
      <div className="flex items-start gap-2 mb-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          hasClient ? 'bg-green-100' : hasPhone ? 'bg-blue-100' : 'bg-gray-100'
        }`}>
          <Users className={`w-4 h-4 ${hasClient ? 'text-green-700' : hasPhone ? 'text-blue-700' : 'text-gray-500'}`} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {person.first_name} {person.last_name}
          </p>
          <p className="text-[10px] text-gray-400">Person #{person.id}</p>
        </div>
      </div>

      <div className="space-y-1.5 text-xs">
        <div className="flex items-center gap-1.5">
          <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
          {hasPhone ? (
            <span className="text-gray-700 font-medium">{person.phone_number}</span>
          ) : (
            <span className="text-gray-300 italic">No phone</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
          {person.email ? (
            <span className="text-gray-700 truncate">{person.email}</span>
          ) : (
            <span className="text-gray-300 italic">No email</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="text-gray-700">{person.interaction_count || 0} interactions</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
          {hasAppts ? (
            <span className="text-green-700 font-medium">{person.appointment_count} appointments</span>
          ) : (
            <span className="text-gray-300">No appointments</span>
          )}
        </div>
        {hasClient && (
          <div className="flex items-center gap-1.5">
            <CreditCard className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span className={person.has_card ? 'text-green-700' : 'text-amber-600'}>
              {person.has_card ? 'Card on file' : 'No card'}
            </span>
          </div>
        )}
      </div>

      {person.last_interaction && (
        <div className="mt-2 bg-gray-50 rounded px-2 py-1.5 border border-gray-100">
          <p className="text-[10px] text-gray-400 mb-0.5">Last interaction</p>
          <p className="text-xs text-gray-600 line-clamp-2">{person.last_interaction}</p>
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-1">
        {hasClient && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">Client</span>}
        {hasPhone && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">Has Phone</span>}
        {hasAppts && <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">{person.appointment_count} appts</span>}
        {(person.interaction_count || 0) > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">{person.interaction_count} calls</span>}
      </div>
    </div>
  );
}

function DedupPage() {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    setLoading(true);
    try {
      const data = await apiService.get('/amn/leads/duplicate-persons/');
      setDuplicates(data.duplicates || []);
    } catch (err) {
      console.error('Failed to load duplicates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async (keepId, mergeId, idx) => {
    setProcessing(idx);
    try {
      await apiService.post('/amn/leads/merge-persons/', { keep_id: keepId, merge_id: mergeId });
      setDuplicates(prev => prev.filter((_, i) => i !== idx));
    } catch (err) {
      alert('Merge failed: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessing(null);
    }
  };

  const handleDismiss = async (personAId, personBId, idx) => {
    setProcessing(idx);
    try {
      await apiService.post('/amn/leads/dismiss-duplicate/', { person_a_id: personAId, person_b_id: personBId });
      setDuplicates(prev => prev.filter((_, i) => i !== idx));
    } catch (err) {
      alert('Dismiss failed: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 leading-none">Duplicate Review</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Review and merge duplicate person records to keep lead tracking accurate
            </p>
          </div>
          <button onClick={loadDuplicates} disabled={loading}
            className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50">
            Refresh
          </button>
        </div>

        {loading ? (
          <Panel className="p-8"><LoadingSpinner /></Panel>
        ) : duplicates.length === 0 ? (
          <Panel className="p-8 text-center">
            <Check className="w-10 h-10 mx-auto text-green-500 mb-2" />
            <p className="text-sm font-medium text-gray-900">No duplicates found</p>
            <p className="text-xs text-gray-500 mt-0.5">All person records look clean</p>
          </Panel>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-gray-500">{duplicates.length} potential duplicate{duplicates.length !== 1 ? 's' : ''} found</div>
            {duplicates.map((pair, idx) => {
              const a = pair.person_a;
              const b = pair.person_b;
              const aHasClient = !!a.has_client;
              const bHasClient = !!b.has_client;
              const recommendKeepA = aHasClient && !bHasClient;
              const recommendKeepB = bHasClient && !aHasClient;

              // Default merge direction: keep client record, merge the other
              const defaultKeepId = recommendKeepB ? b.id : a.id;
              const defaultMergeId = recommendKeepB ? a.id : b.id;

              return (
                <Panel key={`${a.id}-${b.id}`} className="!p-0 overflow-hidden">
                  <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                    <Users className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-semibold text-amber-800">
                      Potential match: {a.first_name} {a.last_name}
                    </span>
                    {pair.match_reason && (
                      <span className="text-[10px] text-amber-600 ml-auto">{pair.match_reason}</span>
                    )}
                  </div>

                  <div className="p-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <PersonCard person={a} isRecommendedKeep={recommendKeepA} />
                      <PersonCard person={b} isRecommendedKeep={recommendKeepB} />
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => handleMerge(defaultKeepId, defaultMergeId, idx)}
                        disabled={processing === idx}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 transition-colors"
                      >
                        <Check className="w-3.5 h-3.5" />
                        {processing === idx ? 'Merging...' : 'Merge'}
                        <span className="text-green-200 text-[10px]">
                          (keep #{defaultKeepId})
                        </span>
                      </button>

                      {/* Alternate merge direction */}
                      {(aHasClient || bHasClient) && (
                        <button
                          onClick={() => handleMerge(defaultMergeId, defaultKeepId, idx)}
                          disabled={processing === idx}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-white border border-green-300 text-green-700 rounded-md hover:bg-green-50 disabled:opacity-50 transition-colors"
                        >
                          <ArrowRight className="w-3.5 h-3.5" />
                          Keep other (#{defaultMergeId})
                        </button>
                      )}

                      <button
                        onClick={() => handleDismiss(a.id, b.id, idx)}
                        disabled={processing === idx}
                        className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors ml-auto"
                      >
                        <X className="w-3.5 h-3.5" />
                        Not the same person
                      </button>
                    </div>
                  </div>
                </Panel>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DedupPage;
